import React from 'react'

import { Box, Heading4, Join, Space, useTheme } from '@gousto-internal/citrus-react'
import { useDispatch } from 'react-redux'

import { Recipe, RecipeCoreId, RecipeUUID } from '@library/menu-service'
import { MenuRecipeOption, MenuRecipe } from '@library/menu-service/queries/menuApiMenuRecipe/types'
import { AlternativeOptions, RecipeOptionsIngredients } from '@library/recipe-customisations'

import { useRecipes } from 'hooks/menu-recipes/useRecipes'
import { menuRecipeDetailVisibilityChange } from 'routes/Menu/actions/menuRecipeDetails'

import { useTrackVariantListDisplay } from '../../RecipeAlternativeOptions/RecipeAlternativeOptions/useTracking'
import { RecipeOptionsItem } from './RecipeOptionsItem'

import css from './RecipeOptions.css'

const preventPropagation = (e: React.SyntheticEvent) => e.stopPropagation()

const compareCoreRecipeIds = (a: Recipe, b: Recipe) =>
  parseInt(a.coreId, 10) - parseInt(b.coreId, 10)

type Props = {
  recipeUuid: RecipeUUID
  fullRecipeOptions?: MenuRecipeOption[]
  optionsType?: MenuRecipe['options_type']
  ingredientSlots?: MenuRecipe['ingredient_slots']
  baseRecipeId: RecipeCoreId
}

export const RecipeOptions = ({
  recipeUuid,
  fullRecipeOptions,
  optionsType,
  ingredientSlots,
  baseRecipeId,
}: Props) => {
  const { theme } = useTheme()
  const dispatch = useDispatch()
  const onChange = (checkedRecipeId: string) => {
    dispatch(menuRecipeDetailVisibilityChange(checkedRecipeId))
  }
  const hasAlternativeOptions = (fullRecipeOptions && fullRecipeOptions?.length > 1) || false

  useTrackVariantListDisplay({
    hasAlternativeOptions,
    view: 'details',
  })

  const optionsMenuRecipes = useRecipes(
    (fullRecipeOptions || []).map((option) => option.item_id),
  ).filter((recipe): recipe is Recipe => recipe !== null)

  const orderedOptions = optionsMenuRecipes.sort(compareCoreRecipeIds)

  const options: AlternativeOptions['options'] = orderedOptions.map((recipe) => ({
    recipe,
    recipeId: recipe.coreId,
    isChecked: recipeUuid === recipe.id,
    isOutOfStock: !recipe.isAvailable,
    surcharge: recipe.surcharge ? recipe.surcharge.pricePerPortion / 100 : null,
    ingredientSlotValues: fullRecipeOptions?.find((option) => option.item_id === recipe.id)
      ?.ingredient_slot_values,
  }))

  if (!hasAlternativeOptions) {
    return null
  }

  if (optionsType === 'ingredients') {
    return (
      <RecipeOptionsIngredients
        view="view_recipe_detail_screen"
        baseRecipeId={baseRecipeId}
        recipeUuid={recipeUuid}
        options={options}
        ingredientSlots={ingredientSlots}
        onChangeSelection={onChange}
      />
    )
  }

  return (
    <Box style={{ marginBottom: theme.spacing(4) }}>
      <div
        className={css.recipeList}
        role="button"
        tabIndex={-1}
        onClick={preventPropagation}
        onKeyPress={preventPropagation}
      >
        <Join with={<Space size={4} />}>
          <Heading4>Choose an option</Heading4>
          <ul className={css.recipeListText}>
            {fullRecipeOptions?.map((option) => (
              <RecipeOptionsItem
                key={option.item_id}
                recipeId={option.item_id}
                recipeName={option.name}
                changeCheckedRecipe={() => onChange(option.item_id)}
                isChecked={recipeUuid === option.item_id}
                isOutOfStock={!option.is_available}
                surcharge={option.surcharge?.price_per_portion}
              />
            ))}
          </ul>
        </Join>
      </div>
    </Box>
  )
}
